import { forwardRef } from 'react';
import { type VariantProps, cva } from 'class-variance-authority';

import classNames from '@/utils/classnames';

export const inputVariants = cva(
  'w-full bg-surface-2 border text-fg placeholder:text-fg-disabled outline-none transition-colors focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      inputSize: {
        8: 'h-8 rounded-md px-2.5 text-xs',
        9: 'h-9 rounded-lg px-3 text-sm',
      },
      error: {
        true: 'border-danger focus:border-danger focus:ring-danger/20',
        false: 'border-line focus:border-brand-border focus:ring-brand/15',
      },
    },
    defaultVariants: {
      inputSize: 9,
      error: false,
    },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'className'>, VariantProps<typeof inputVariants> {
  className?: string;
  /** Icon rendered inside the field, left-aligned (e.g. a search glyph). */
  iconLeft?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ inputSize, error, className, iconLeft, ...props }, ref) => {
    if (iconLeft) {
      return (
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted">{iconLeft}</span>
          <input ref={ref} className={classNames(inputVariants({ inputSize, error }), 'pl-9', className)} {...props} />
        </div>
      );
    }

    return <input ref={ref} className={classNames(inputVariants({ inputSize, error }), className)} {...props} />;
  },
);
Input.displayName = 'Input';
